import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Brain, Loader2, Check, AlertTriangle, FileSpreadsheet, Database, Download, Sparkles } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BulkImport() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState('tenants');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [importDone, setImportDone] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setImportDone(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_type', importType);

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/import/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setResult(data);
    } catch (error) { console.error('Import failed:', error); }
    finally { setLoading(false); }
  };

  const handleConfirmImport = async () => {
    if (!result?.ai_validation) return;
    setConfirming(true);
    try {
      // Parse AI structured data
      let aiData = result.ai_validation;
      if (typeof aiData === 'string') {
        try { aiData = JSON.parse(aiData); } catch { /* ignore */ }
      }

      const records = aiData?.structured_data || result.sample_raw || [];

      const formData = new FormData();
      formData.append('import_type', importType);
      formData.append('data', JSON.stringify(records));

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/import/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setImportDone(data);
    } catch (error) { console.error('Confirm failed:', error); }
    finally { setConfirming(false); }
  };

  const typeLabels = isAr
    ? { tenants: 'مستأجرين', properties: 'عقارات', units: 'وحدات', leases: 'عقود إيجار', payments: 'مدفوعات', auto: 'كشف تلقائي' }
    : { tenants: 'Tenants', properties: 'Properties', units: 'Units', leases: 'Leases', payments: 'Payments', auto: 'Auto-detect' };

  // Downloadable CSV templates per type
  const TEMPLATES = {
    tenants: 'full_name,email,phone,national_id,nationality\nAhmad Al-Rashid,ahmad@example.com,+966501234567,1012345678,Saudi\nSara Al-Mutairi,sara@example.com,+966502345678,1023456789,Saudi',
    properties: 'name,name_ar,type,address,city,total_units,year_built\nAl-Yasmin Tower,برج الياسمين,residential,Olaya District,Riyadh,24,2019\nMarina Plaza,مارينا بلازا,mixed,Cornish Road,Jeddah,16,2021',
    units: 'unit_number,unit_type,size_sqm,monthly_rent,floor,bedrooms,bathrooms,status\n101,2BR,95,5500,1,2,2,vacant\n102,3BR,130,7800,1,3,2,occupied',
    leases: 'tenant_email,property_name,unit_number,start_date,end_date,monthly_rent,deposit,payment_frequency\nahmad@example.com,Al-Yasmin Tower,101,2025-01-01,2026-01-01,5500,11000,monthly',
    payments: 'tenant_name,amount,due_date,status,payment_method,paid_date\nAhmad Al-Rashid,5500,2025-01-05,paid,bank_transfer,2025-01-04\nSara Al-Mutairi,4800,2025-01-05,overdue,,'
  };

  const downloadTemplate = (type) => {
    const content = TEMPLATES[type] || '';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `REVA_${type}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const seedClients = async () => {
    if (!window.confirm(isAr ? 'هذا سيُنشئ/يُعيد بناء 10 منظمات عملاء تجريبية. متابعة؟' : 'This will create/reset 10 demo client organizations. Continue?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/seed/clients`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.summary) {
        alert(`${data.message}\n\n` +
          `Properties: ${data.summary.properties}\nUnits: ${data.summary.units}\nTenants: ${data.summary.tenants}\nLeases: ${data.summary.leases}\nPayments: ${data.summary.payments}\nUsers: ${data.summary.users_created}\n\nLogin: client1@reva.com … client10@reva.com / Test1234!`);
      } else {
        alert(data.detail || data.message || 'Done');
      }
    } catch (e) { alert('Seed failed: ' + e.message); }
  };

  const parseAIResult = () => {
    if (!result?.ai_validation) return null;
    const data = result.ai_validation;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return null; }
    }
    return data;
  };

  const aiParsed = parseAIResult();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="h-5 w-5 text-teal-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'استيراد جماعي' : 'Bulk Import'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'ارفع ملف CSV أو Excel لاستيراد بيانات بالجملة مع تحقق ذكي' : 'Upload CSV/Excel to bulk import data with AI validation'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => downloadTemplate(importType)} variant="outline" size="sm" className="rounded-xl" data-testid="bulk-download-template">
              <Download className="h-3.5 w-3.5 me-1.5" /> {isAr ? `قالب ${typeLabels[importType] || ''}` : `${typeLabels[importType] || ''} template`}
            </Button>
            <Button onClick={seedClients} size="sm" className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-seed-clients">
              <Sparkles className="h-3.5 w-3.5 me-1.5" /> {isAr ? 'تجهيز 10 عملاء تجريبيين' : 'Seed 10 Demo Clients'}
            </Button>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { step: '1', title: isAr ? 'رفع الملف' : 'Upload', desc: 'CSV / Excel', icon: Upload },
            { step: '2', title: isAr ? 'تحقق AI' : 'AI Validates', desc: isAr ? 'تنظيف وهيكلة' : 'Clean & structure', icon: Brain },
            { step: '3', title: isAr ? 'مراجعة' : 'Review', desc: isAr ? 'تأكيد البيانات' : 'Confirm data', icon: AlertTriangle },
            { step: '4', title: isAr ? 'استيراد' : 'Import', desc: isAr ? 'حفظ في قاعدة البيانات' : 'Save to database', icon: Database }
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="shadow-soft border-gray-100">
                <CardContent className="pt-4 pb-3 text-center">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mx-auto mb-2 text-white text-xs font-bold">{s.step}</div>
                  <Icon className="h-4 w-4 text-gray-400 mx-auto mb-1" />
                  <div className="font-medium text-xs text-gray-900">{s.title}</div>
                  <div className="text-[10px] text-gray-500">{s.desc}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Upload Panel (2 cols) */}
          <Card className="lg:col-span-2 shadow-soft border-gray-100">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">{isAr ? 'رفع الملف' : 'Upload File'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">{isAr ? 'نوع البيانات' : 'Data Type'}</Label>
                <Select value={importType} onValueChange={setImportType}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-primary/40 transition-colors cursor-pointer" onClick={() => document.getElementById('bulk-file').click()}>
                <input id="bulk-file" type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={e => { setFile(e.target.files[0]); setResult(null); setImportDone(null); }} />
                <FileSpreadsheet className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-primary">{file.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500">{isAr ? 'اضغط لاختيار ملف' : 'Click to select file'}</p>
                    <p className="text-xs text-gray-400 mt-1">.csv, .xlsx, .xls</p>
                  </div>
                )}
              </div>

              <Button onClick={handleUpload} disabled={!file || loading} className="w-full gradient-primary text-white rounded-xl">
                {loading ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Brain className="h-4 w-4 me-2" />}
                {isAr ? 'تحليل وتحقق' : 'Analyze & Validate'}
              </Button>

              {/* Import confirmation */}
              {result && !importDone && (
                <Button onClick={handleConfirmImport} disabled={confirming} variant="outline" className="w-full rounded-xl border-green-300 text-green-700 hover:bg-green-50">
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Database className="h-4 w-4 me-2" />}
                  {isAr ? 'تأكيد الاستيراد إلى قاعدة البيانات' : 'Confirm Import to Database'}
                </Button>
              )}

              {importDone && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">{importDone.message}</span>
                  </div>
                  <p className="text-xs text-green-600">{importDone.inserted}/{importDone.total_submitted} {isAr ? 'سجل تم استيراده' : 'records imported'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Panel (3 cols) */}
          <Card className="lg:col-span-3 shadow-soft border-gray-100">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">{isAr ? 'نتائج التحقق' : 'Validation Results'}</CardTitle></CardHeader>
            <CardContent>
              {loading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
                  <p className="text-sm text-gray-500">{isAr ? 'جاري تحليل الملف...' : 'Analyzing file...'}</p>
                </div>
              )}

              {!loading && !result && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <FileSpreadsheet className="h-12 w-12 text-gray-300 mb-4" />
                  <p className="text-sm text-gray-400 max-w-xs">{isAr ? 'ارفع ملف CSV أو Excel للتحقق من البيانات' : 'Upload a CSV/Excel file to validate data'}</p>
                </div>
              )}

              {!loading && result && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-blue-50 rounded-xl text-center">
                      <div className="text-lg font-bold text-blue-700">{result.total_rows_parsed}</div>
                      <div className="text-xs text-blue-600">{isAr ? 'صفوف' : 'Rows'}</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-xl text-center">
                      <div className="text-lg font-bold text-green-700">{aiParsed?.valid_rows || result.total_rows_parsed}</div>
                      <div className="text-xs text-green-600">{isAr ? 'صحيحة' : 'Valid'}</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-xl text-center">
                      <div className="text-lg font-bold text-red-700">{aiParsed?.invalid_rows || 0}</div>
                      <div className="text-xs text-red-600">{isAr ? 'خاطئة' : 'Invalid'}</div>
                    </div>
                  </div>

                  {/* Column mapping */}
                  {result.raw_columns && (
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{isAr ? 'الأعمدة المكتشفة' : 'Detected Columns'}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {result.raw_columns.map((col, i) => (
                          <span key={i} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-700">{col}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Issues */}
                  {aiParsed?.issues_found?.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
                        <span className="text-xs font-semibold text-yellow-700">{isAr ? 'مشاكل مكتشفة' : 'Issues Found'}</span>
                      </div>
                      {aiParsed.issues_found.map((issue, i) => (
                        <div key={i} className="text-xs text-yellow-800 py-0.5">- {issue}</div>
                      ))}
                    </div>
                  )}

                  {/* Sample Data */}
                  {result.sample_raw?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{isAr ? 'عينة من البيانات' : 'Data Sample'}</div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {Object.keys(result.sample_raw[0]).map(k => (
                                <TableHead key={k} className="text-xs whitespace-nowrap">{k}</TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {result.sample_raw.map((row, i) => (
                              <TableRow key={i}>
                                {Object.values(row).map((v, j) => (
                                  <TableCell key={j} className="text-xs">{v != null ? String(v) : '-'}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* AI structured result */}
                  {aiParsed?.structured_data && (
                    <div className="p-3 bg-primary/5 border border-primary/10 rounded-xl">
                      <div className="text-xs font-semibold text-primary mb-2">{isAr ? 'البيانات المهيكلة بالذكاء الاصطناعي' : 'AI Structured Data'}</div>
                      <div className="text-xs font-mono max-h-[200px] overflow-y-auto whitespace-pre-wrap">
                        {JSON.stringify(aiParsed.structured_data.slice(0, 5), null, 2)}
                      </div>
                    </div>
                  )}

                  {/* Confidence */}
                  {aiParsed?.confidence && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{isAr ? 'مستوى الثقة:' : 'Confidence:'}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        aiParsed.confidence === 'high' ? 'bg-green-100 text-green-700' :
                        aiParsed.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>{aiParsed.confidence}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
